import { useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import {
  DefaultGuests,
  type DetailSearchParams,
  type GuestsState,
  KyrgyzstanEmpty,
  KyrgyzstanLoader,
  SelectGuestsForm,
  kyrgyzstanDetailContextStore,
  kyrgyzstanDetailSelectionStore,
  useRealtyCapacity,
} from 'entities/kyrgyzstan'

import { HeaderGoBack } from 'shared/ui/header-go-back'

import s from './styles.module.scss'

const getInitialGuests = (
  detailSearchParams: DetailSearchParams | null,
): GuestsState => {
  const childrenAges = detailSearchParams?.children_ages ?? []

  return {
    adults: detailSearchParams?.visitors_count ?? DefaultGuests.adults,
    children: childrenAges.length,
    childrenAges,
  }
}

const KyrgyzstanSelectGuestsBookingPage = observer(() => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { effectiveRealty, restoreSelection } = kyrgyzstanDetailSelectionStore
  const { detailSearchParams, restoreSavedContext, setBookingGuests } =
    kyrgyzstanDetailContextStore
  const initialGuests = getInitialGuests(detailSearchParams)

  useEffect(() => {
    restoreSavedContext()
    restoreSelection()
  }, [restoreSavedContext, restoreSelection])

  const objectId = effectiveRealty?.id

  const { capacity, isLoading: isLoadingCapacity } = useRealtyCapacity({
    objectId,
    startDate: detailSearchParams?.start_date,
    endDate: detailSearchParams?.end_date,
  })
  const isCapacityUnavailable =
    capacity?.available_rooms === 0 || capacity?.max_guests === 0

  const handleGoBack = () => {
    navigate(-1)
  }

  const handleSubmit = (guestsState: GuestsState) => {
    setBookingGuests(guestsState)

    navigate(-1)
  }

  return (
    <div className={clsx('container', s.container)}>
      <HeaderGoBack
        buttonWhite
        title={t('kyrgyzstan_block.select_guests.title')}
        variant="grey"
        onGoBack={handleGoBack}
      />

      {isLoadingCapacity ? (
        <KyrgyzstanLoader />
      ) : isCapacityUnavailable ? (
        <KyrgyzstanEmpty
          title={t('kyrgyzstan_block.select_guests.unavailable_title')}
          subtitle={t('kyrgyzstan_block.select_guests.unavailable_subtitle')}
        />
      ) : capacity?.max_guests ? (
        <SelectGuestsForm
          key={`${initialGuests.adults}-${initialGuests.children}-${initialGuests.childrenAges.join(',')}`}
          initialState={initialGuests}
          maxGuests={capacity?.max_guests}
          capacity={capacity}
          isShowInfo
          showSubmitButton
          onSubmit={handleSubmit}
        />
      ) : (
        <KyrgyzstanEmpty title={t('kyrgyzstan_block.error.title')} />
      )}
    </div>
  )
})

export default KyrgyzstanSelectGuestsBookingPage
